import axios from 'axios';

import { configSetting } from '../config';
import { authActions } from '../redux/slices/authSlice';
import store from '../redux/store';

// Plain (non-hook) helpers used by the axios interceptors, which run outside
// React's render tree and can't call useHttps()/useAppDispatch().

let refreshPromise: Promise<string> | null = null;

const persistTokens = (accessToken: string, refreshToken: string) => {
  store.dispatch(authActions.setTokens({ accessToken, refreshToken }));

  const raw = localStorage.getItem('authData');
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      parsed.accessToken = { token: accessToken };
      parsed.refreshToken = { token: refreshToken };
      localStorage.setItem('authData', JSON.stringify(parsed));
    } catch {
      // Corrupt/absent local copy — the Redux store is still authoritative
      // for the rest of this session.
    }
  }
};

export const REFRESH_TOKEN_URL = 'api/v1/auth/admin/refresh';

/** Exchanges the stored refresh token for a new access/refresh pair, sharing
 * a single in-flight request across concurrent 401s so only one refresh call
 * is ever made at a time. Throws if there is no refresh token, or the
 * refresh call itself fails (expired/revoked token). */
export const refreshAccessToken = async (): Promise<string> => {
  if (refreshPromise) {
    return refreshPromise;
  }

  const currentRefreshToken = store.getState().auth.authData?.refreshToken?.token;
  if (!currentRefreshToken) {
    return Promise.reject(new Error('No refresh token available'));
  }

  refreshPromise = axios
    .post(
      `${configSetting.apiUrl}${REFRESH_TOKEN_URL}`,
      { refresh_token: currentRefreshToken },
      { headers: { 'Content-Type': 'application/json' } }
    )
    .then((response) => {
      const { access_token, refresh_token } = response.data.data;
      persistTokens(access_token, refresh_token);
      return access_token as string;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};
